'use client';

import * as React from 'react';

import { Cross2Icon } from '@radix-ui/react-icons';
import { Dialog as DialogPrimitive } from 'radix-ui';

import { cn } from '../lib/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

/**
 * ===========================================================================
 * 🔴 `duration-200` AN DER HUELLE — SIE WAR SCHNELLER ALS DIE PLATTE
 * ===========================================================================
 *
 * Bauteil-Inventur vom 20.09.2026, Befund 4: „Die Abdunklung ist schneller als
 * die Platte, die sie abdunkelt. Platte 200 ms (Dialog) bzw. 220/260 ms
 * (Sheet), Huelle jeweils 150 ms. Der Hintergrund ist dunkel, bevor der Dialog
 * da ist."
 *
 * Die 150 ms standen hier nirgends — sie sind der Vorgabewert von
 * `tailwindcss-animate` fuer `animate-in`/`animate-out`, und genau deshalb hat
 * sie niemand gesehen: an der Platte steht eine Zahl, an der Huelle stand
 * keine. Eine fehlende Angabe sieht nicht aus wie eine abweichende.
 *
 * ⚠️ DIE 200 SIND NICHT NACHGEZOGEN, SONDERN UEBERNOMMEN. A9 nennt „Ein- und
 * Ausblenden 160 ms"; ob diese Zahl aus dem Board-Zusammenhang auch fuer
 * Dialoge gilt, sagt kein Blatt — die Inventur fuehrt das als offene Frage
 * D-3 ans Design. Solange sie offen ist, gilt hier die Regel „ein Wert je
 * Vorgang", nicht „mein Wert": die Huelle bekommt die Zahl IHRER Platte
 * (`:51`, `duration-200`). Wer D-3 beantwortet, aendert beide zusammen.
 */
const DialogOverlay: React.FC<
  React.ComponentPropsWithRef<typeof DialogPrimitive.Overlay>
> = ({ className, ...props }) => (
  <DialogPrimitive.Overlay
    className={cn(
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 bg-overlay fixed inset-0 z-50 backdrop-blur-[2px] duration-200',
      className,
    )}
    {...props}
  />
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  overlayClassName?: string;
  hideCloseButton?: boolean;
  closeLabel?: string;
}

const DialogContent: React.FC<DialogContentProps> = ({
  className,
  children,
  overlayClassName,
  hideCloseButton,
  closeLabel,
  ...props
}) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      className={cn(
        'bg-card data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 shadow-dialog sm:rounded-card fixed top-[50%] left-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 p-6 duration-200',
        className,
      )}
      {...props}
    >
      {children}
      {!hideCloseButton && (
        <DialogPrimitive.Close className="text-muted-2 hover:text-foreground absolute top-4 right-4 transition-colors disabled:pointer-events-none">
          <Cross2Icon className="h-4 w-4" />
          <span className="sr-only">{closeLabel ?? 'Close'}</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col space-y-1.5 text-left', className)}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle: React.FC<
  React.ComponentPropsWithRef<typeof DialogPrimitive.Title>
> = ({ className, ...props }) => (
  <DialogPrimitive.Title
    className={cn(
      'text-lg leading-none font-semibold tracking-tight',
      className,
    )}
    {...props}
  />
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription: React.FC<
  React.ComponentPropsWithRef<typeof DialogPrimitive.Description>
> = ({ className, ...props }) => (
  <DialogPrimitive.Description
    className={cn('text-muted-foreground text-sm', className)}
    {...props}
  />
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
